import { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { dashboardAPI, platformIntegrationsAPI, advertisersAPI, currencyAPI, customMetricsAPI } from '../lib/api';
import { formatNumber, formatCurrency, formatPercentage, getPlatformColor, getCurrencyList, getCurrencySymbol } from '../lib/utils';
import { useAuth } from '../lib/auth';
import { usePlatformAccess } from '../hooks/usePlatformAccess';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  Eye,
  MousePointer,
  DollarSign,
  Target,
  TrendingUp,
  TrendingDown,
  Calendar as CalendarIcon,
  Loader2,
  Download,
  PlayCircle,
  PauseCircle,
  CheckCircle2,
  Layers,
  Wifi,
  WifiOff,
  RefreshCw,
  Clock,
  AlertCircle,
  Building2,
  Users,
} from 'lucide-react';
import { format, subDays, formatDistanceToNow } from 'date-fns';

const CURRENCY_LIST = getCurrencyList();

const MetricCard = ({ title, value, icon: Icon, trend, trendValue, loading }) => (
  <Card className="metric-card border-slate-200">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</p>
          {loading ? (
            <div className="h-8 w-24 bg-slate-200 animate-pulse rounded mt-2" />
          ) : (
            <p className="text-2xl font-mono font-medium text-slate-900 mt-1">{value}</p>
          )}
        </div>
        <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
          <Icon className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
        </div>
      </div>
      {trend !== undefined && !loading && (
        <div className="flex items-center gap-1 mt-3">
          {trend >= 0 ? (
            <TrendingUp className="w-4 h-4 text-emerald-600" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-600" />
          )}
          <span className={`text-xs font-medium ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {trend >= 0 ? '+' : ''}{trendValue}
          </span>
          <span className="text-xs text-slate-500">vs last period</span>
        </div>
      )}
    </CardContent>
  </Card>
);

export const Dashboard = () => {
  const { user, isSuperAdmin, isAdmin, isAdvertiser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [platformData, setPlatformData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [platformHealth, setPlatformHealth] = useState([]);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthStats, setHealthStats] = useState({ healthy: 0, unhealthy: 0 });
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  
  // Advertiser filter for Admin/Super Admin
  const [advertisers, setAdvertisers] = useState([]);
  const [selectedAdvertiser, setSelectedAdvertiser] = useState('all');
  const canFilterByAdvertiser = isSuperAdmin() || isAdmin();
  const isAdvertiserUser = isAdvertiser();
  const isAdvertiserRole = user?.role === 'advertiser';
  const isUserRole = user?.role === 'user';
  
  // User filter for Advertiser role (to view user-wise data)
  const [advertiserUsers, setAdvertiserUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('all');
  
  // For advertiser/user roles, automatically use their advertiser_id
  const effectiveAdvertiserId = (isAdvertiserUser || isUserRole)
    ? user?.advertiser_id 
    : (selectedAdvertiser !== 'all' ? selectedAdvertiser : null);
  
  // Get currency from selected advertiser or default to USD
  const selectedAdvertiserData = useMemo(() => {
    if (isAdvertiserUser && user?.advertiser_id) {
      return advertisers.find(a => a.id === user.advertiser_id);
    }
    if (selectedAdvertiser !== 'all') {
      return advertisers.find(a => a.id === selectedAdvertiser);
    }
    return null;
  }, [advertisers, selectedAdvertiser, isAdvertiserUser, user?.advertiser_id]);
  
  const displayCurrency = selectedAdvertiserData?.currency || 'USD';
  
  // Unified currency view state (for admins only)
  const [unifiedViewEnabled, setUnifiedViewEnabled] = useState(false);
  const [unifiedCurrency, setUnifiedCurrency] = useState('USD');
  const [unifiedData, setUnifiedData] = useState(null);
  const [unifiedLoading, setUnifiedLoading] = useState(false);
  
  // Custom metrics state
  const [customMetrics, setCustomMetrics] = useState([]);
  const [customMetricValues, setCustomMetricValues] = useState({});
  
  // Fetch unified spend data when unified view is enabled
  const fetchUnifiedData = async () => {
    if (!unifiedViewEnabled || !canFilterByAdvertiser) return;
    
    setUnifiedLoading(true);
    try {
      const response = await currencyAPI.getUnifiedSpend(unifiedCurrency, {
        start_date: format(dateRange.from, 'yyyy-MM-dd'),
        end_date: format(dateRange.to, 'yyyy-MM-dd'),
      });
      setUnifiedData(response.data);
    } catch (error) {
      console.error('Failed to fetch unified spend data:', error);
      toast.error('Failed to fetch unified currency data');
    } finally {
      setUnifiedLoading(false);
    }
  };
  
  useEffect(() => {
    if (unifiedViewEnabled) {
      fetchUnifiedData();
    }
  }, [unifiedViewEnabled, unifiedCurrency, dateRange]);
  
  // Effective currency and summary based on view mode
  const effectiveCurrency = unifiedViewEnabled ? unifiedCurrency : displayCurrency;
  const effectiveSummary = useMemo(() => {
    if (unifiedViewEnabled && unifiedData) {
      return {
        total_spend: unifiedData.total_spend,
        total_impressions: unifiedData.total_impressions,
        total_clicks: unifiedData.total_clicks,
        total_conversions: unifiedData.total_conversions,
        avg_ctr: unifiedData.avg_ctr,
        avg_cvr: unifiedData.avg_cvr,
        avg_cpc: unifiedData.avg_cpc,
        avg_cpm: unifiedData.avg_cpm,
      };
    }
    return summary;
  }, [unifiedViewEnabled, unifiedData, summary]);
  
  // Platform access filtering
  const { effectivePlatforms, filterByPlatform, isPlatformAllowed } = usePlatformAccess(effectiveAdvertiserId);
  
  // Filter platform data and health based on allowed platforms
  const filteredPlatformData = useMemo(() => 
    filterByPlatform(platformData, 'platform'),
    [platformData, filterByPlatform]
  );
  
  const filteredPlatformHealth = useMemo(() => 
    platformHealth.filter(p => isPlatformAllowed(p.platform)),
    [platformHealth, isPlatformAllowed]
  );
  
  const filteredHealthStats = useMemo(() => ({
    healthy: filteredPlatformHealth.filter(p => p.is_healthy !== false && p.connection_status === 'connected').length,
    unhealthy: filteredPlatformHealth.filter(p => p.is_healthy === false || p.connection_status === 'error').length
  }), [filteredPlatformHealth]);
  
  // Fetch advertisers for filter dropdown (only for admins)
  useEffect(() => {
    if (canFilterByAdvertiser) {
      advertisersAPI.getAll()
        .then(res => setAdvertisers(res.data || []))
        .catch(err => console.error('Failed to fetch advertisers:', err));
    }
  }, [canFilterByAdvertiser]);
  
  // Fetch users under advertiser (for advertiser role to see user-wise data)
  useEffect(() => {
    if (isAdvertiserRole && user?.advertiser_id) {
      advertisersAPI.getUsers(user.advertiser_id)
        .then(res => setAdvertiserUsers(res.data || []))
        .catch(err => console.error('Failed to fetch advertiser users:', err));
    }
  }, [isAdvertiserRole, user?.advertiser_id]);

  const fetchPlatformHealth = async () => {
    setHealthLoading(true);
    try {
      const response = await platformIntegrationsAPI.getHealth();
      const data = response.data;
      setPlatformHealth(data.platforms || []);
      setHealthStats({
        healthy: data.healthy_count || 0,
        unhealthy: data.unhealthy_count || 0
      });
    } catch (error) {
      console.error('Failed to fetch platform health:', error);
      // Fallback to basic integration list
      try {
        const fallbackResponse = await platformIntegrationsAPI.getAll();
        setPlatformHealth(fallbackResponse.data || []);
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setHealthLoading(false);
    }
  };

  const triggerHealthCheck = async () => {
    try {
      await platformIntegrationsAPI.triggerHealthCheck();
      toast.success('Health check triggered - alerts will be sent if issues are found');
      fetchPlatformHealth();
    } catch (error) {
      toast.error('Failed to trigger health check');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: format(dateRange.from, 'yyyy-MM-dd'),
        end_date: format(dateRange.to, 'yyyy-MM-dd'),
      };
      
      // Add advertiser filter (auto for advertiser users, manual selection for admins)
      if (effectiveAdvertiserId) {
        params.advertiser_id = effectiveAdvertiserId;
      }
      
      // Add user filter for advertiser role
      if (isAdvertiserRole && selectedUserId && selectedUserId !== 'all') {
        params.filter_user_id = selectedUserId;
      }
      
      // Filter by platforms for user role (only show their assigned platforms)
      if (user?.role === 'user' && effectivePlatforms.length > 0 && effectivePlatforms.length < 8) {
        params.platforms = effectivePlatforms.join(',');
      }

      const [summaryRes, platformRes, trendRes] = await Promise.all([
        dashboardAPI.getSummary(params),
        dashboardAPI.getPlatformSummary(params),
        dashboardAPI.getTrend(params),
      ]);

      setSummary(summaryRes.data);
      setPlatformData(platformRes.data);
      setTrendData(trendRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchPlatformHealth();
  }, [dateRange, effectiveAdvertiserId, selectedUserId, effectivePlatforms]);

  // Fetch custom metrics
  useEffect(() => {
    const fetchCustomMetrics = async () => {
      try {
        const response = await customMetricsAPI.getAll('dashboard');
        const metrics = response.data?.filter(m => m.is_active && m.show_on_dashboard) || [];
        setCustomMetrics(metrics);
      } catch (error) {
        console.error('Failed to fetch custom metrics:', error);
      }
    };
    fetchCustomMetrics();
  }, []);

  // Calculate custom metric values when summary data changes
  useEffect(() => {
    if (!summary || customMetrics.length === 0) return;
    
    const calculateMetricValues = async () => {
      try {
        const baseData = {
          impressions: summary.total_impressions || 0,
          clicks: summary.total_clicks || 0,
          spend: summary.total_spend || 0,
          conversions: summary.total_conversions || 0,
          ctr: summary.avg_ctr || 0,
          cpc: summary.avg_cpc || 0,
          cpm: summary.avg_cpm || 0,
        };
        
        const metricIds = customMetrics.map(m => m.id);
        const response = await customMetricsAPI.calculate(baseData, metricIds.join(','));
        setCustomMetricValues(response.data?.calculated_values || {});
      } catch (error) {
        console.error('Failed to calculate custom metrics:', error);
      }
    };
    
    calculateMetricValues();
  }, [summary, customMetrics]);

const handleExportDashboard = () => {
    // Generate comprehensive dashboard CSV report
    let csv = '';
    
    // Report Header
    csv += `Report PRO - Dashboard Report\n`;
    csv += `Date Range: ${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}\n`;
    csv += `Generated: ${format(new Date(), 'MMM d, yyyy HH:mm')}\n`;
    if (selectedAdvertiser && selectedAdvertiser !== 'all') {
      const adv = advertisers.find(a => a.id === selectedAdvertiser);
      csv += `Advertiser: ${adv?.name || 'Unknown'}\n`;
    }
    csv += `\n`;
    
    // Summary Section
    csv += `SUMMARY METRICS\n`;
    csv += `Metric,Value\n`;
    csv += `Total Impressions,${formatNumber(summary?.total_impressions || 0)}\n`;
    csv += `Total Clicks,${formatNumber(summary?.total_clicks || 0)}\n`;
    csv += `Total Spend,${formatCurrency(summary?.total_spend || 0, effectiveCurrency)}\n`;
    csv += `Total Conversions,${formatNumber(summary?.total_conversions || 0)}\n`;
    csv += `Active Campaigns,${summary?.campaign_status?.active || 0}\n`;
    csv += `Paused Campaigns,${summary?.campaign_status?.paused || 0}\n`;
    csv += `Total Campaigns,${summary?.campaign_status?.total || 0}\n`;
    csv += `\n`;
    
    // Platform Breakdown
    csv += `PLATFORM BREAKDOWN\n`;
    csv += `Platform,Impressions,Clicks,CTR,Spend,CPC,CPM,Conversions\n`;
    filteredPlatformData.forEach(p => {
      csv += `${p.platform},${p.impressions},${p.clicks},${p.ctr}%,${formatCurrency(p.spend || 0, effectiveCurrency)},$${(p.cpc || 0).toFixed(2)},$${(p.cpm || 0).toFixed(2)},${p.conversions || 0}\n`;
    });
    csv += `\n`;
    
    // Daily Trend
    csv += `DAILY PERFORMANCE TREND\n`;
    csv += `Date,Impressions,Clicks,CTR,Spend,Conversions\n`;
    trendData.forEach(day => {
      const dayCTR = day.impressions > 0 ? ((day.clicks / day.impressions) * 100).toFixed(2) : 0;
      csv += `${format(new Date(day.date), 'yyyy-MM-dd')},${day.impressions},${day.clicks},${dayCTR}%,${formatCurrency(day.spend || 0, effectiveCurrency)},${day.conversions || 0}\n`;
    });
    csv += `\n`;
    
    // Unified Spend by Advertiser (if admin viewing all)
    if (unifiedViewEnabled && unifiedSpendData.length > 0) {
      csv += `SPEND BY ADVERTISER (Unified: ${unifiedCurrency})\n`;
      csv += `Advertiser,Original Currency,Original Spend,Converted Spend (${unifiedCurrency})\n`;
      unifiedSpendData.forEach(adv => {
        csv += `${adv.name || 'Unassigned'},${adv.currency},${formatCurrency(adv.original_spend, adv.currency)},${formatCurrency(adv.converted_spend, unifiedCurrency)}\n`;
      });
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Dashboard_Report_${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Dashboard report exported successfully');
  };

  const CHART_COLORS = ['#0F172A', '#059669', '#0284C7', '#D97706', '#7C3AED', '#DB2777', '#06B6D4', '#84CC16', '#F43F5E'];

  return (
    <Layout>
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            {isAdvertiserUser ? (
              <>
                <h1 className="font-heading text-3xl font-bold text-slate-900">
                  Your Dashboard
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                  Campaign performance for your account
                </p>
              </>
            ) : (
              <>
                <h1 className="font-heading text-3xl font-bold text-slate-900">Dashboard</h1>
                <p className="text-sm text-slate-500 mt-1">Campaign performance overview</p>
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Advertiser Filter for Admin/Super Admin (not shown for advertiser users) */}
            {canFilterByAdvertiser && advertisers.length > 0 && (
              <Select value={selectedAdvertiser} onValueChange={setSelectedAdvertiser}>
                <SelectTrigger className="w-[200px]" data-testid="advertiser-filter">
                  <Building2 className="w-4 h-4 mr-2 text-slate-500" />
                  <SelectValue placeholder="All Advertisers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Advertisers</SelectItem>
                  {advertisers.map((adv) => (
                    <SelectItem key={adv.id} value={adv.id}>
                      {adv.name} ({adv.currency || 'USD'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Unified Currency View Toggle for Admins */}
            {canFilterByAdvertiser && selectedAdvertiser === 'all' && (
              <div className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2">
                  <Switch
                    id="unified-currency"
                    checked={unifiedViewEnabled}
                    onCheckedChange={setUnifiedViewEnabled}
                    data-testid="unified-currency-toggle"
                  />
                  <Label htmlFor="unified-currency" className="text-sm font-medium text-slate-700 cursor-pointer">
                    Unified View
                  </Label>
                </div>
                {unifiedViewEnabled && (
                  <Select value={unifiedCurrency} onValueChange={setUnifiedCurrency}>
                    <SelectTrigger className="w-[100px]" data-testid="unified-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_LIST.map((currency) => (
                        <SelectItem key={currency.code} value={currency.code}>
                          {currency.symbol} {currency.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
            
            {/* User Filter for Advertiser Role - Only for primary advertisers without platform restrictions */}
            {isAdvertiserRole && advertiserUsers.length > 0 && !user?.allowed_platforms?.length && (
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="w-[200px]" data-testid="user-filter">
                  <Users className="w-4 h-4 mr-2 text-slate-500" />
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {advertiserUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
{/* Export Dashboard Report */}
            <Button
              variant="outline"
              onClick={handleExportDashboard}
              data-testid="export-dashboard-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="min-w-[240px] justify-start text-left" data-testid="date-range-btn">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {format(dateRange.from, 'MMM d, yyyy')} - {format(dateRange.to, 'MMM d, yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => {
                    if (range?.from) {
                      // Allow single day selection or range selection
                      setDateRange({
                        from: range.from,
                        to: range.to || range.from  // If no 'to', use 'from' as single day
                      });
                    }
                  }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Impressions"
            value={formatNumber(summary?.total_impressions || 0)}
            icon={Eye}
            loading={loading}
          />
          <MetricCard
            title="Total Clicks"
            value={formatNumber(summary?.total_clicks || 0)}
            icon={MousePointer}
            loading={loading}
          />
          <MetricCard
            title={`Total Spend (${effectiveCurrency})`}
            value={formatCurrency(effectiveSummary?.total_spend || 0, effectiveCurrency)}
            icon={DollarSign}
            loading={loading || (unifiedViewEnabled && unifiedLoading)}
          />
          <MetricCard
            title="Total Conversions"
            value={formatNumber(summary?.total_conversions || 0)}
            icon={Target}
            loading={loading}
          />
        </div>

        {/* Campaign Status Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                  <Layers className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-mono font-semibold text-slate-900">
                    {loading ? '—' : summary?.campaign_counts?.total || 0}
                  </p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Total Campaigns</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center">
                  <PlayCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-mono font-semibold text-emerald-700">
                    {loading ? '—' : summary?.campaign_counts?.active || 0}
                  </p>
                  <p className="text-xs text-emerald-600 uppercase tracking-wider">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center">
                  <PauseCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-mono font-semibold text-amber-700">
                    {loading ? '—' : summary?.campaign_counts?.paused || 0}
                  </p>
                  <p className="text-xs text-amber-600 uppercase tracking-wider">Paused</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-300 bg-slate-50/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-200 rounded-sm flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-mono font-semibold text-slate-700">
                    {loading ? '—' : summary?.campaign_counts?.completed || 0}
                  </p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Avg. CTR</p>
              <p className="text-2xl font-mono font-medium text-slate-900 mt-1">
                {loading ? '—' : formatPercentage(summary?.avg_ctr || 0)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Avg. CPC</p>
              <p className="text-2xl font-mono font-medium text-slate-900 mt-1">
                {loading ? '—' : formatCurrency(summary?.avg_cpc || 0, displayCurrency)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Avg. CPM</p>
              <p className="text-2xl font-mono font-medium text-slate-900 mt-1">
                {loading ? '—' : formatCurrency(summary?.avg_cpm || 0, displayCurrency)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Custom Metrics */}
        {customMetrics.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                <Target className="w-4 h-4" />
                Custom Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {customMetrics.map((metric) => (
                  <div key={metric.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{metric.name}</p>
                    <p className="text-2xl font-mono font-medium text-slate-900 mt-1">
                      {loading ? '—' : (
                        customMetricValues[metric.id] !== undefined
                          ? metric.format_type === 'percentage'
                            ? `${customMetricValues[metric.id]?.toFixed(2)}%`
                            : metric.format_type === 'currency'
                            ? formatCurrency(customMetricValues[metric.id], displayCurrency)
                            : typeof customMetricValues[metric.id] === 'number'
                            ? customMetricValues[metric.id].toLocaleString(undefined, { maximumFractionDigits: 2 })
                            : customMetricValues[metric.id]
                          : '—'
                      )}
                    </p>
                    {metric.description && (
                      <p className="text-xs text-slate-400 mt-1">{metric.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Platform Connection Health - Hidden for advertiser users */}
        {!isAdvertiserUser && (
        <Card className="border-slate-200" data-testid="platform-health-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                  <Wifi className="w-4 h-4" />
                  Platform Connection Health
                </CardTitle>
                {!healthLoading && (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                      <CheckCircle2 className="w-3 h-3" />
                      {filteredHealthStats.healthy} Healthy
                    </span>
                    {filteredHealthStats.unhealthy > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 animate-pulse">
                        <AlertCircle className="w-3 h-3" />
                        {filteredHealthStats.unhealthy} Issue{filteredHealthStats.unhealthy > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {(isSuperAdmin() || user?.role === 'admin') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={triggerHealthCheck}
                    className="h-8 px-2 text-xs"
                    title="Trigger health check & send alerts"
                  >
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Check
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchPlatformHealth}
                  disabled={healthLoading}
                  className="h-8 px-2"
                >
                  <RefreshCw className={`w-4 h-4 ${healthLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {healthLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="h-4 bg-slate-200 animate-pulse rounded mb-2" />
                    <div className="h-3 bg-slate-200 animate-pulse rounded w-2/3" />
                  </div>
                ))
              ) : filteredPlatformHealth.length > 0 ? (
                filteredPlatformHealth.map((platform) => {
                  const isHealthy = platform.is_healthy !== false;
                  const isConnected = platform.connection_status === 'connected';
                  const hasError = platform.connection_status === 'error' || !isHealthy;
                  const lastSync = platform.last_sync ? new Date(platform.last_sync) : null;
                  const issue = platform.issue;
                  
                  return (
                    <div
                      key={platform.platform}
                      className={`p-3 rounded-lg border transition-all ${
                        hasError
                          ? 'border-red-200 bg-red-50/50'
                          : isConnected
                          ? 'border-emerald-200 bg-emerald-50/50'
                          : 'border-slate-200 bg-slate-50'
                      }`}
                      data-testid={`platform-health-${platform.platform.toLowerCase()}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {hasError ? (
                          <AlertCircle className="w-4 h-4 text-red-600" />
                        ) : isConnected ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <WifiOff className="w-4 h-4 text-slate-400" />
                        )}
                        <span className="font-medium text-sm text-slate-900 truncate">
                          {platform.platform}
                        </span>
                      </div>
                      {issue ? (
                        <div className="text-xs text-red-600 font-medium truncate">
                          {issue}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-xs">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-500 truncate">
                            {lastSync
                              ? formatDistanceToNow(lastSync, { addSuffix: true })
                              : 'Never synced'}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full text-center py-4 text-slate-400 text-sm">
                  No platforms configured. Go to Integrations to connect platforms.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Trend Chart */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Spend Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {loading ? (
                  <div className="h-full flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : trendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: '#64748B' }}
                        tickFormatter={(value) => format(new Date(value), 'MMM d')}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#64748B' }}
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip
                        formatter={(value) => formatCurrency(value, effectiveCurrency)}
                        labelFormatter={(label) => format(new Date(label), 'MMM d, yyyy')}
                      />
                      <Line
                        type="monotone"
                        dataKey="spend"
                        stroke="#0F172A"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                    No data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Platform Distribution */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Spend by Platform
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {loading ? (
                  <div className="h-full flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : filteredPlatformData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={filteredPlatformData}
                        dataKey="spend"
                        nameKey="platform"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ platform, percent }) =>
                          `${platform} ${(percent * 100).toFixed(2)}%`
                        }
                        labelLine={false}
                      >
                        {filteredPlatformData.map((entry, index) => (
                          <Cell
                            key={entry.platform}
                            fill={CHART_COLORS[index % CHART_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value, effectiveCurrency)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                    No data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Unified Advertiser Spend Breakdown (only when unified view enabled) */}
        {unifiedViewEnabled && unifiedData && (
          <Card className="border-slate-200 border-l-4 border-l-blue-500" data-testid="unified-spend-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Spend by Advertiser (Unified in {unifiedCurrency})
                </CardTitle>
                <div className="text-sm text-slate-500">
                  {unifiedData.currencies_included?.length || 0} currencies converted
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Advertiser</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Original Currency</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Original Spend</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Converted ({unifiedCurrency})</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Impressions</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unifiedLoading ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          {Array.from({ length: 6 }).map((_, j) => (
                            <td key={j} className="py-3 px-4">
                              <div className="h-4 bg-slate-200 animate-pulse rounded" />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : unifiedData.advertisers?.length > 0 ? (
                      unifiedData.advertisers.map((adv) => (
                        <tr key={adv.advertiser_id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 font-medium text-slate-900">{adv.advertiser_name}</td>
                          <td className="py-3 px-4">
                            <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-xs">
                              {adv.original_currency}
                            </span>
                          </td>
                          <td className="text-right py-3 px-4 font-mono text-slate-600">
                            {formatCurrency(adv.original_spend, adv.original_currency)}
                          </td>
                          <td className="text-right py-3 px-4 font-mono text-slate-900 font-medium">
                            {formatCurrency(adv.converted_spend, unifiedCurrency)}
                          </td>
                          <td className="text-right py-3 px-4 font-mono text-slate-700">
                            {formatNumber(adv.impressions)}
                          </td>
                          <td className="text-right py-3 px-4 font-mono text-slate-700">
                            {formatNumber(adv.clicks)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-slate-400">
                          No advertiser data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {unifiedData.advertisers?.length > 0 && (
                    <tfoot>
                      <tr className="bg-slate-50 font-semibold">
                        <td className="py-3 px-4 text-slate-900">Total</td>
                        <td className="py-3 px-4 text-xs text-slate-500">
                          ({unifiedData.currencies_included?.join(', ')})
                        </td>
                        <td className="text-right py-3 px-4"></td>
                        <td className="text-right py-3 px-4 font-mono text-slate-900">
                          {formatCurrency(unifiedData.total_spend, unifiedCurrency)}
                        </td>
                        <td className="text-right py-3 px-4 font-mono text-slate-700">
                          {formatNumber(unifiedData.total_impressions)}
                        </td>
                        <td className="text-right py-3 px-4 font-mono text-slate-700">
                          {formatNumber(unifiedData.total_clicks)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Platform Performance Table */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              Platform Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Platform</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Impressions</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Clicks</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">CTR</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Spend</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">CPC</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Conversions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        {Array.from({ length: 7 }).map((_, j) => (
                          <td key={j} className="py-3 px-4">
                            <div className="h-4 bg-slate-200 animate-pulse rounded" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : platformData.length > 0 ? (
                    platformData.map((platform) => (
                      <tr
                        key={platform.platform}
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: getPlatformColor(platform.platform) }}
                            />
                            <span className="font-medium text-slate-900">{platform.platform}</span>
                          </div>
                        </td>
                        <td className="text-right py-3 px-4 font-mono text-slate-700">
                          {formatNumber(platform.impressions)}
                        </td>
                        <td className="text-right py-3 px-4 font-mono text-slate-700">
                          {formatNumber(platform.clicks)}
                        </td>
                        <td className="text-right py-3 px-4 font-mono text-slate-700">
                          {formatPercentage(platform.ctr)}
                        </td>
                        <td className="text-right py-3 px-4 font-mono text-slate-700">
                          {formatCurrency(platform.spend, displayCurrency)}
                        </td>
                        <td className="text-right py-3 px-4 font-mono text-slate-700">
                          {formatCurrency(platform.cpc, displayCurrency)}
                        </td>
                        <td className="text-right py-3 px-4 font-mono text-slate-700">
                          {formatNumber(platform.conversions)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-slate-400">
                        No platform data available. Click "Seed Demo Data" to generate sample data.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
